import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Flame, Droplets, Zap, Shield, Activity, Thermometer, Wind, ArrowDown, Move, Heart } from 'lucide-react';
const reasons = [{
  icon: Droplets,
  title: "Dr. Sebi Didn't Believe in Numbing Pain - He Believed in Restoring Flow",
  content: "Dr. Sebi was clear: \"If blood can't reach it, it can't heal.\" Painkillers silence signals. They don't fix the cause. Ginext follows Sebi's philosophy by focusing on warming circulation, not dulling nerves. When blood flow increases, oxygen and nutrients reach the joint - and the body can finally do its job.",
  image: 'https://cdn.magicpatterns.com/uploads/aios6Kta28bhzZyF5J4vWV/heylloyd-image-1767424863577.png?v=20250102b'
}, {
  icon: Flame,
  title: 'Ginger Creates Deep, Natural Heat That Activates Healing',
  content: "Ginger isn't a painkiller. It's a circulatory stimulant. Ginger creates a deep warming effect that expands blood vessels, increases oxygen delivery, and helps flush stagnant waste from joint tissue. This warmth isn't surface-level. It penetrates deep - exactly where joints become stiff and undernourished.",
  image: 'https://cdn.magicpatterns.com/uploads/x6JBdrQLU3XkVTxkubgW3Q/heylloyd-image-1767423575420.png?v=20250102b'
}, {
  icon: Zap,
  title: "Black Pepper Unlocks Ginger's Full Power",
  content: 'Ginger alone is powerful. But ginger + black pepper is where the magic happens. Black pepper contains piperine, which increases absorption dramatically, helps ginger penetrate deeper into tissue, and amplifies warming and circulation effects. Dr. Sebi always emphasized synergy. Nothing works alone. Ginext follows that rule.',
  image: 'https://cdn.magicpatterns.com/uploads/mqJz7shkFWWbsUFYSLvfxJ/heylloyd-image-1767423541922.png?v=20250102b'
}, {
  icon: Shield,
  title: 'Eucalyptus Calms Overactive Pain Signals - Without Numbing You',
  content: "Most topical products numb sensation. That's not healing - that's shutdown. Eucalyptus works differently. It helps calm inflammatory signaling and reduce discomfort perception while maintaining natural sensation. You still feel your body - just without the overload. This aligns with Dr. Sebi's belief that the body must stay awake to heal.",
  image: 'https://cdn.magicpatterns.com/uploads/6Y9MeqmY4d5YoaenR1ocqC/heylloyd-image-1767423611152.png?v=20250102b'
}, {
  icon: Activity,
  title: 'Rosemary Supports Circulation and Tissue Recovery',
  content: 'Rosemary is a circulatory tonic. It helps stimulate blood flow, support muscle and joint recovery, and reduce stiffness caused by poor circulation. In traditional herbal systems, rosemary was used to "wake up" cold, tight areas of the body - exactly what stiff joints need.',
  image: 'https://cdn.magicpatterns.com/uploads/jfDMk3pK5bcPzoHPkWHVPi/heylloyd-image-1767423639751.png?v=20250102b'
}, {
  icon: Thermometer,
  title: 'Cold Joints = Stagnant Joints (Dr. Sebi Warned About This)',
  content: "Dr. Sebi often explained that cold is a sign of poor circulation. When joints stay cold, nutrients can't enter, waste can't exit, and tissue becomes rigid. Ginext Ginger Oil restores natural warmth, which signals the body to send blood, oxygen, and nutrients back into the area.",
  image: 'https://cdn.magicpatterns.com/uploads/vdxuL3NKyjHTbFQfFqEks5/heylloyd-image-1767423682082.png'
}, {
  icon: Wind,
  title: 'Botanical Oils Absorb Fast - No Greasy Residue',
  content: 'Ginext uses clean botanical carrier oils that absorb quickly into the skin, deliver actives deep into tissue, and leave no greasy or sticky residue. That means you can apply it and move - no waiting, no mess, no artificial fillers blocking absorption.',
  image: 'https://cdn.magicpatterns.com/uploads/o2NWMSRGMw2RjSEtJMaW2P/heylloyd-image-1767423760947.png'
}, {
  icon: ArrowDown,
  title: 'Topical Delivery Bypasses the Digestive Bottleneck',
  content: 'Dr. Sebi warned that many supplements never reach their target. Topical application avoids digestion issues, goes straight to the affected area, and delivers results faster. Ginext works where you need it, not hours later through your stomach.',
  image: 'https://cdn.magicpatterns.com/uploads/hZYhVa1Z7wgzV2cqZDCaR2/heylloyd-image-1767424247595.png'
}, {
  icon: Move,
  title: 'Relief That Supports Movement - Not Dependency',
  content: 'Most pain products train you to reapply constantly, depend on numbness, and fear movement. Ginext is designed to support natural movement, encouraging circulation every time you use it - not locking joints into stillness.',
  image: 'https://cdn.magicpatterns.com/uploads/vri4iTP2U5EA7KGRo7yQQy/heylloyd-image-1767424342558.png'
}, {
  icon: Heart,
  title: 'Your Body Was Built to Heal - It Just Needs Flow',
  content: "Dr. Sebi believed the body is self-healing when conditions are right. Ginext doesn't override your body. It supports it by restoring warmth, circulation, nutrient delivery, and tissue mobility. That's the difference between masking pain and restoring function.",
  image: 'https://cdn.magicpatterns.com/uploads/2WNLNpzR3JXQVxVVGb4La5/heylloyd-image-1767424376857.png'
}];
export function DrSebiReasonsSection() {
  return <section className="py-12 bg-white" data-id="element-674">
      <div className="max-w-3xl mx-auto px-6" data-id="element-675">
        <div className="space-y-16" data-id="element-676">
          {reasons.map((reason, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="border-b border-[#DDDDDD] pb-8" data-id="element-677">
              {reason.image && <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true,
            margin: '0px'
          }} transition={{
            duration: 0.5
          }} className="mb-6" data-id="element-678">
                  <img src={reason.image} alt={reason.title} className="w-full h-auto" loading={index < 3 ? 'eager' : 'lazy'} data-id="element-679" />
                </motion.div>}

              <div className="flex items-start gap-4 mb-3" data-id="element-680">
                <span className="text-2xl font-bold text-[#CC0000] flex-shrink-0" data-id="element-681">
                  {index + 1}.
                </span>
                <h2 className="text-xl sm:text-2xl font-bold text-black leading-tight" data-id="element-682">
                  {reason.title}
                </h2>
              </div>

              <div className="text-[#333333] leading-relaxed" data-id="element-683">
                <p data-id="element-684">{reason.content}</p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}